import { PartialTenant, Tenant } from '@/core/tenant';
import { PropsWithChildren, createContext, useEffect, useState } from 'react';

export interface LocalStorageRoot {
  region?: 'EU' | 'US';
  regionSuccess?: boolean;
  tenant?: Tenant | null;
  lastUser?: string | null;
  recentlyViewedTenants: {
    [authIdentifier: string]: PartialTenant[];
  };
  isProduction?: boolean;
  sessionExpireIn?: number;
  g2NextPromptDate?: string;
  metabaseToken?: { [id: number]: string };
}

export type LocalStorageType = <Key extends keyof LocalStorageRoot>(
  key: Key,
  value: LocalStorageRoot[Key]
) => void;

interface LocalStorageContextType {
  data: Partial<LocalStorageRoot>;
  setLocalStorage: LocalStorageType;
}

const LocalStorageContext = createContext<LocalStorageContextType | undefined>(
  undefined
);

function LocalStorageProvider({ children }: PropsWithChildren) {
  const [lsValue, setLsValue] = useState<Partial<LocalStorageRoot>>(() => {
    let initialValue = {};
    const plainLS = localStorage.getItem('wll');
    if (plainLS) {
      try {
        initialValue = JSON.parse(plainLS) || {};
      } catch {
        initialValue = {};
      }
    }
    return initialValue;
  });

  useEffect(() => {
    if (!lsValue.region) {
      lsValue.regionSuccess = false;
    }

    localStorage.setItem('wll', JSON.stringify(lsValue));
  }, [lsValue]);

  const setLocalStorage = <Key extends keyof LocalStorageRoot>(
    key: Key,
    value: LocalStorageRoot[Key]
  ) => {
    setLsValue((prevValue) => ({ ...prevValue, [key]: value }));
  };

  return (
    <LocalStorageContext.Provider value={{ data: lsValue, setLocalStorage }}>
      {children}
    </LocalStorageContext.Provider>
  );
}

export { LocalStorageContext, LocalStorageProvider };
